import React, { useState, useEffect } from 'react';
import SectionBasePage from 'components/SectionBasePage';
import { Button } from 'UI';
import { Form } from 'antd';
import { useDispatch } from 'hooks/store';
import { createClientAction } from 'state/slices/clients';
import { useBreadcrumbNavigation } from 'hooks/breadcrumb';
import { createAlertNotification } from 'infra/notification';
import { AlertNotificationType, setNotificationAction } from 'state/slices/notification';
import { unwrapResult } from '@reduxjs/toolkit';
import { PlusOutlined } from '@ant-design/icons';
import { CreateClientData } from 'api/client';

import BasicClientForm, { AddClientBasicFormData } from './components/BasicClientForm';
import PaymentClientForm, { AddClientPaymentFormData } from './components/PaymentClientForm';
import LocationModal, { AddClientLocationFormData } from './components/LocationModal';
import LocationClientList from './components/LocationClientList';
import {
  MainContainer,
  PaymentCollapse,
} from './style';

const ClientAddPage = (): JSX.Element => {
  const [basicInfoForm] = Form.useForm<AddClientBasicFormData>();
  const [paymentInfoForm] = Form.useForm<AddClientPaymentFormData>();
  const [locationInfoForm] = Form.useForm<AddClientLocationFormData>();
  const [isLoading, setLoading] = useState(false);
  const navigator = useBreadcrumbNavigation();
  const dispatch = useDispatch();
  const [
    locationsToAdd,
    setLocationsToAdd,
  ] = useState<(AddClientLocationFormData & { isActive: boolean })[]>([]);
  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number>();

  // const onLocationEdit = (tableIndex: number) => {
  //   setEditIndex(tableIndex);
  // };

  const onLocationDelete = (tableIndex: number) => {
    const updatedLocationsToAdd = locationsToAdd
      .map((loc, index) => (
        index !== tableIndex
          ? ({
            ...loc,
            key: index,
          })
          : undefined
      ))
      .filter((loc) => !!loc) as (AddClientLocationFormData & { isActive: boolean })[];
    setLocationsToAdd(updatedLocationsToAdd);
  };

  useEffect(() => {
    if (editIndex !== undefined && editIndex >= 0) {
      setModalVisible(true);
    }
  }, [editIndex]);

  const editingLocation = editIndex !== undefined && editIndex >= 0
    ? locationsToAdd[editIndex]
    : undefined;

  const closeModal = () => {
    locationInfoForm.resetFields();
    setEditIndex(undefined);
    setModalVisible(false);
  };

  const onModalSubmit = (data: AddClientLocationFormData) => {
    const isExistingLocation = editIndex !== undefined && editIndex >= 0;
    const updatedLocationsToAdd = locationsToAdd.map((loc, index) => {
      const updatedLocation = isExistingLocation && editIndex === index ? ({
        ...data,
        key: index,
        isActive: true,
      }) : ({
        ...loc,
        key: index,
      });
      return updatedLocation;
    });
    if (!isExistingLocation) {
      updatedLocationsToAdd.push({
        ...data,
        key: updatedLocationsToAdd.length,
        isActive: true,
      });
    }
    setLocationsToAdd(updatedLocationsToAdd);
    closeModal();
  };

  const onSaveClick = async () => {
    basicInfoForm.validateFields();
    paymentInfoForm.validateFields();

    const basicData = basicInfoForm.getFieldsValue();
    const paymentData = paymentInfoForm.getFieldsValue();
    const clientFullData: CreateClientData = {
      ...basicData,
      ...paymentData,
      paymentIsUpfront: paymentData.paymentIsUpfront === 'true',
      locations: locationsToAdd,
      isActive: true,
    };

    try {
      setLoading(true);
      const result = await dispatch(createClientAction(clientFullData));
      const createdClient = await unwrapResult(result);
      const successAlert = createAlertNotification(`
        Cliente ${createdClient.name} criado com sucesso.
      `, AlertNotificationType.Success);
      dispatch(setNotificationAction(successAlert));
      setLoading(false);
      navigator.toPrevious();
    } catch (err) {
      const errorAlert = createAlertNotification('Erro interno ao tentar criar cliente. Tente novamente.', AlertNotificationType.Error);
      dispatch(setNotificationAction(errorAlert));
      setLoading(false);
    }
  };

  return (
    <SectionBasePage
      title="Novo cliente"
      extra={(
        <Button
          loading={isLoading}
          onClick={onSaveClick}
        >
          Salvar cliente
        </Button>
      )}
    >
      <MainContainer>
        {/* BASIC INFO */}
        <BasicClientForm
          formHandler={basicInfoForm}
          isLoading={isLoading}
        />

        {/* PAYMENT INFO */}
        <PaymentCollapse ghost defaultActiveKey="payment">
          <PaymentCollapse.Panel header="Informações de pagamento" key="payment">
            <PaymentClientForm
              formHandler={paymentInfoForm}
              isLoading={isLoading}
            />
          </PaymentCollapse.Panel>
        </PaymentCollapse>

        {/* LOCATIONS INFO */}
        {/* BUG: new location after edit comes with previous edit info */}
        <LocationModal
          formHandler={locationInfoForm}
          onCancel={closeModal}
          onSubmit={onModalSubmit}
          isVisible={isModalVisible}
          curLocation={editingLocation}
        />
        <PaymentCollapse ghost defaultActiveKey="locations">
          <PaymentCollapse.Panel
            header="Localizações"
            key="locations"
            extra={(
              <Button
                icon={<PlusOutlined size={14} />}
                disabled={isLoading}
                onClick={(e) => {
                  setEditIndex(undefined);
                  setModalVisible(true);
                  e.stopPropagation();
                }}
              >
                Adicionar localização
              </Button>
            )}
          >
            <LocationClientList
              locationsToAdd={locationsToAdd}
              isLoading={isLoading}
              // onEdit={onLocationEdit}
              onDelete={onLocationDelete}
            />
          </PaymentCollapse.Panel>
        </PaymentCollapse>
      </MainContainer>
    </SectionBasePage>
  );
};

export default ClientAddPage;
